// src/utils/collabHighlights.js
//
// View-only "Collaborator Edits" decorations for the editor. When the View-tab
// toggle is on, text that people have edited is tinted in that person's color,
// so you can see at a glance who wrote what.
//
// TWO RENDERING MODES per paragraph:
//   1. SEGMENT mode (preferred) — each run of text is tinted by whoever actually
//      typed it, so a single line can carry several colors. Used only when the
//      paragraph is plain text (no inline formatting) and is NOT the paragraph
//      the local caret sits in.
//   2. BLOCK mode (fallback) — the whole block gets a left-border + background.
//      One editor → solid color; several → gradient. Used for rich paragraphs,
//      the actively-edited paragraph, or when per-char attribution can't be
//      reconstructed exactly.
//
// HOW ATTRIBUTION IS COMPUTED: edits arrive as an ordered list of
// { userId, username, paragraphIndex, raw } events (raw = the paragraph's full
// text right after that edit). For each paragraph we REPLAY its events in order,
// diffing the running text against each event's `raw` (common prefix + common
// suffix) and attributing the changed span to that event's author. The result
// is a per-character author array we group into colored runs. Replaying from
// scratch each render keeps this stateless and self-correcting.
//
// WHY INDEX, NOT TEXT MATCHING: keying on paragraph POSITION lets two people who
// edit the SAME paragraph both stay attributed to it. (An earlier text-matching
// version lost the first editor the moment the second changed the text.)
//
// SAFETY (manuscripts must never be corrupted):
//   - All decorations carry HIGHLIGHT_ATTR so getCleanHTML() strips them
//     losslessly; they are NEVER persisted or broadcast.
//   - Segment mode wraps text nodes in <span>s. To stay lossless it runs ONLY on
//     paragraphs with no element children — unwrapping the spans returns the
//     exact original text. Rich paragraphs use block mode (attrs only).
//   - The caret's paragraph is never segment-wrapped, so typing is undisturbed.
//   - Callers CLEAR before any structural DOM op (pagination) and re-apply.

import { HIGHLIGHT_ATTR } from './editorSerialize';
// Shared with commentHighlights.js — decorating a block destroys the text nodes
// a live caret points at, so both passes record and restore it the same way.
import { caretOffsetIn, restoreCaretIn } from './caretPreserve';

const KIND = 'collab';
const HL_CLASS = 'rv-collab-highlight';

const SKIP_SEL = '.rv-end-spacer, .rv-page-header, .rv-page-footer';

// The flat, document-ordered set of block elements we diff and tint. This MUST
// be identical to the selector useEditorSocket uses to compute paragraphIndex,
// or the indexes won't line up — so both import this single constant.
export const COLLAB_BLOCK_SEL =
  '.rv-page-body > p, .rv-page-body > h1, .rv-page-body > h2, .rv-page-body > h3, .rv-page-body > h4, .rv-page-body > li';

// A palette of well-separated hues so distinct users get visibly distinct
// colors (the old raw-hash-to-hue could land two users on near-identical hues).
// Deterministic: the same userId maps to the same slot on every client, so a
// person keeps one color across all screens (barring a rare hash collision).
const PALETTE_HUES = [210, 0, 145, 35, 275, 190, 330, 90, 255, 20, 165, 300];

/**
 * Deterministic per-user color, snapped to a distinct palette hue.
 * @param {string|null} userId
 * @returns {{ fill: string, border: string, solid: string }}
 */
export function colorForUser(userId) {
  const key = String(userId || 'anon');
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  const hue = PALETTE_HUES[h % PALETTE_HUES.length];
  return {
    fill: `hsla(${hue}, 80%, 62%, 0.42)`, // translucent tint behind text
    border: `hsl(${hue}, 70%, 42%)`,      // strong accent (left border)
    solid: `hsla(${hue}, 80%, 60%, 0.55)`, // per-word segment fill
  };
}

/**
 * Remove every collaborator-edit decoration from the live DOM, restoring the
 * original content exactly. Handles both modes: block-level attrs/styles are
 * stripped; segment <span>s are unwrapped and adjacent text nodes re-merged.
 * Idempotent and lossless.
 * @param {Element|null} root editor host
 */
export function clearCollabHighlights(root) {
  if (!root) return;
  let unwrapped = false;
  root.querySelectorAll(`[${HIGHLIGHT_ATTR}="${KIND}"]`).forEach((el) => {
    if (el.tagName === 'SPAN') {
      // Segment wrapper — move children out, drop the span.
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      unwrapped = true;
    } else {
      // Block-level tint — strip decoration attrs/styles, keep the element.
      el.removeAttribute(HIGHLIGHT_ATTR);
      el.classList.remove(HL_CLASS);
      el.style.background = '';
      el.style.backgroundColor = '';
      el.style.borderLeft = '';
      el.style.borderImage = '';
      el.style.paddingLeft = '';
      // removeAttribute, not `= ''`: blanking leaves title="" behind, which
      // then gets serialized into the saved document as litter.
      el.removeAttribute('title');
      if (el.classList.length === 0) el.removeAttribute('class');
    }
  });
  // Merge the text nodes left behind by unwrapping so later diffs/length math
  // see clean, single text nodes.
  if (unwrapped) root.normalize();
}

/**
 * Longest common prefix / suffix between two strings. Returns [p, s] where the
 * changed region of `next` is next.slice(p, next.length - s).
 */
function prefixSuffix(prev, next) {
  const a = prev || '';
  const b = next || '';
  let p = 0;
  const max = Math.min(a.length, b.length);
  while (p < max && a[p] === b[p]) p++;
  let s = 0;
  while (s < max - p && a[a.length - 1 - s] === b[b.length - 1 - s]) s++;
  return [p, s];
}

/**
 * Replay one paragraph's edit events into a per-character author array.
 * @param {Array} events  edits for a single paragraphIndex, in arrival order
 * @returns {{ authors: (string|null)[], text: string }}
 *          authors[i] = userId that last typed char i of `text` (null = unknown)
 */
function replayAuthors(events) {
  let text = '';
  let authors = [];
  for (const e of events) {
    const next = typeof e.raw === 'string' ? e.raw : '';
    const [p, s] = prefixSuffix(text, next);
    const changedLen = next.length - p - s;
    const head = authors.slice(0, p);
    const mid = new Array(Math.max(0, changedLen)).fill(e.userId ?? null);
    const tail = s > 0 ? authors.slice(text.length - s) : [];
    authors = head.concat(mid, tail);
    text = next;
  }
  return { authors, text };
}

/**
 * (Re)build all collaborator-edit decorations from scratch. Always clears first.
 *
 * @param {Element|null} root         editor host
 * @param {Array}        edits        [{ userId, username, paragraphIndex, raw }]
 * @param {Element|null} activeBlock   block holding the local caret; its caret
 *                                     offset is saved and restored around the
 *                                     re-render so typing isn't disturbed. Pass
 *                                     null if the editor isn't focused.
 */
export function applyCollabHighlights(root, edits, activeBlock = null) {
  if (!root) return;

  // The clear/re-render below rewrites text nodes (unwrapping old segment spans
  // and normalizing). If the caret is sitting in a paragraph that gets rewritten
  // that would jump it, so capture the caret's text offset first and restore it
  // afterwards. Only tracked when the caret is actually inside activeBlock.
  const caretOffset = activeBlock ? caretOffsetIn(activeBlock) : null;

  clearCollabHighlights(root);

  // Group events by paragraph index, preserving arrival order within each group.
  const byIndex = new Map();
  (edits || []).forEach((e) => {
    const idx = e && Number.isInteger(e.paragraphIndex) ? e.paragraphIndex : -1;
    if (idx < 0) return;
    if (!byIndex.has(idx)) byIndex.set(idx, []);
    byIndex.get(idx).push(e);
  });
  if (byIndex.size === 0) return;

  // Per-user display data (color + name) for tooltips and fills.
  const userInfo = (uid, uname) => {
    const c = colorForUser(uid);
    return { ...c, name: uname || 'Collaborator' };
  };

  root.querySelectorAll(COLLAB_BLOCK_SEL).forEach((block, i) => {
    if (block.closest(SKIP_SEL)) return;
    const events = byIndex.get(i);
    if (!events || events.length === 0) return;

    // Distinct editors of this paragraph (for tooltip + block-mode fallback).
    const seen = new Map(); // userId -> info
    events.forEach((e) => {
      const k = String(e.userId ?? 'anon');
      if (!seen.has(k)) seen.set(k, userInfo(e.userId, e.username));
    });
    const editors = Array.from(seen.values());
    const label = editors.length > 1
      ? `Edited by ${editors.slice(0, -1).map((u) => u.name).join(', ')} & ${editors[editors.length - 1].name}`
      : `Edited by ${editors[0].name}`;

    // Prefer SEGMENT mode — per-author colored runs — whenever the paragraph is
    // plain text (no inline elements to flatten) and our replay reproduces the
    // exact DOM text. This runs on the active caret line too: the caret is saved
    // and restored around the re-render, so typing is undisturbed. Block mode is
    // only a fallback for rich paragraphs or a replay/text mismatch.
    const isPlain = block.children.length === 0;
    let rendered = false;

    if (isPlain && editors.length > 0) {
      const { authors, text } = replayAuthors(events);
      if (text === block.textContent && text.length > 0) {
        rendered = renderSegments(block, authors, seen, label);
      }
    }

    if (!rendered) {
      renderBlock(block, editors, label);
    }
  });

  // Put the caret back where it was if its paragraph got rewritten.
  if (caretOffset != null && activeBlock && root.contains(activeBlock)) {
    restoreCaretIn(activeBlock, caretOffset);
  }
}

/**
 * Segment mode: replace the block's single text node with per-author colored
 * <span>s. Returns true on success. Lossless — spans carry HIGHLIGHT_ATTR and
 * contain only the original characters, so getCleanHTML()/clear() restore them.
 */
function renderSegments(block, authors, seen, label) {
  const text = block.textContent;
  if (!text || authors.length !== text.length) return false; // invariant guard
  const frag = document.createDocumentFragment();
  let start = 0;
  let spanCount = 0;
  const flush = (end, uid) => {
    if (end <= start) return;
    const chunk = text.slice(start, end);
    if (uid == null) {
      // Unattributed run (pre-existing text) — leave it plain.
      frag.appendChild(document.createTextNode(chunk));
    } else {
      const info = seen.get(String(uid));
      const span = document.createElement('span');
      span.setAttribute(HIGHLIGHT_ATTR, KIND);
      span.className = HL_CLASS;
      if (info) {
        span.style.backgroundColor = info.solid;
        span.style.boxShadow = `inset 0 -2px 0 ${info.border}`;
        span.title = `Written by ${info.name}`;
      }
      span.appendChild(document.createTextNode(chunk));
      frag.appendChild(span);
      spanCount++;
    }
    start = end;
  };
  const END = Symbol('end');
  let cur = authors[0] ?? null;
  for (let i = 1; i <= authors.length; i++) {
    const a = i < authors.length ? (authors[i] ?? null) : END;
    if (a !== cur) {
      flush(i, cur);
      cur = a;
    }
  }
  if (spanCount === 0) return false; // nothing to color — use block mode instead
  block.textContent = '';
  block.appendChild(frag);
  block.title = label;
  return true;
}

/**
 * Block mode: tint the whole block. One editor → solid; several → gradient.
 */
function renderBlock(block, editors, label) {
  const fills = editors.map((u) => u.fill);
  const borders = editors.map((u) => u.border);

  const blendedFill = fills.length === 1
    ? fills[0]
    : `linear-gradient(135deg, ${fills.join(', ')})`;
  const borderStyle = borders.length === 1
    ? `3px solid ${borders[0]}`
    : '4px solid transparent';
  const borderImage = borders.length > 1
    ? `linear-gradient(to bottom, ${borders.join(', ')}) 1`
    : '';

  block.setAttribute(HIGHLIGHT_ATTR, KIND);
  block.classList.add(HL_CLASS);
  block.style.background = blendedFill;
  block.style.borderLeft = borderStyle;
  if (borderImage) block.style.borderImage = borderImage;
  block.style.paddingLeft = '6px';
  block.title = label;
}
